import { clsx } from 'clsx';
import { useCallback, useRef, useState } from 'react';

import { useMergedRefs } from '@gitbook/util-react-hooks';

import { Tooltip, type TooltipProps } from './Tooltip';

export interface TruncatableProps extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
    children: React.ReactNode;
    tooltipProps?: Omit<TooltipProps, 'content' | 'children'>;
}

/**
 * A component that truncates text content with an ellipsis and displays a tooltip
 * with the full content when the text overflows its container.
 *
 * Carries `data-truncated` while the text is actually cut off, which is the measurement the tooltip
 * is decided from — exposed because it is otherwise unobservable, the tooltip being a collaborator
 * that some environments disable.
 *
 * @param children - The content to be truncated
 * @param tooltipProps - Optional props to customize the tooltip behavior and appearance
 * @param rest - Standard HTML div attributes
 * @returns A div element with truncated text, wrapped in a tooltip if text overflows
 */
export function Truncatable({ ref: propRef, children, tooltipProps, ...rest }: TruncatableProps) {
    const [isEnabled, setIsEnabled] = useState(false);
    const observerRef = useRef<ResizeObserver | null>(null);

    /**
     * Watches whichever element is currently rendered, re-measuring whenever its box changes.
     *
     * A ref callback rather than an effect, because the node being measured is replaced: attaching
     * the tooltip makes `Tooltip` render a `TooltipTrigger` where it previously passed the child
     * straight through, and React swaps the element for a new one. An effect would keep observing
     * the detached node, so the label would never notice a later resize — it would keep the first
     * verdict it ever reached.
     */
    const measureRef = useCallback((element: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        if (!element) {
            return;
        }

        const measure = () => setIsEnabled(element.scrollWidth > element.clientWidth);
        // Measured here as well as on resize: a `ResizeObserver` delivers nothing at all while the
        // page is hidden, so the first verdict must not wait on one.
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        observerRef.current = observer;
    }, []);

    const ref = useMergedRefs(propRef, measureRef);

    const content = (
        <div
            ref={ref}
            {...rest}
            className={clsx('truncate', rest.className)}
            data-truncated={isEnabled || undefined}
        >
            {children}
        </div>
    );

    if (isEnabled) {
        return (
            <Tooltip label={typeof children === 'string' ? children : undefined} {...tooltipProps}>
                {content}
            </Tooltip>
        );
    }

    return content;
}
